<script lang="ts">
	import { browser, dev } from '$app/env';
	import {
		canUpdateServiceWorker,
		isServiceWorkerUpdateNetworkError
	} from '#lib/helper/service-worker.js';
	import { t } from '#lib/stores/i18n.js';
	import { showToast } from '#lib/stores/toast.js';
	import { resetWidget } from '#lib/widget/booking/flow.js';
	import { widgetConfig } from '#lib/widget/core.js';
	import { onDestroy, onMount } from 'svelte';

	let isWidget = $derived(!!$widgetConfig);
	let swScope = $derived(isWidget ? '/widget/' : '/');

	let refreshing = $state(false);
	let alert = $state(false);

	let swRegistration: ServiceWorkerRegistration | null = null;
	let versionCheckInterval: ReturnType<typeof setInterval> | undefined;
	let visibilityTimeout: ReturnType<typeof setTimeout> | undefined;

	function onVisibilityChange() {
		if (visibilityTimeout) {
			clearTimeout(visibilityTimeout);
			visibilityTimeout = undefined;
		}

		if (document.visibilityState === 'visible') {
			// Delay check by 2.5s after wake-up to allow OS network sockets to stabilize
			visibilityTimeout = setTimeout(() => {
				visibilityTimeout = undefined;
				void checkForUpdate();
			}, 2500);
		}
	}

	onDestroy(() => {
		if (versionCheckInterval) clearInterval(versionCheckInterval);
		if (visibilityTimeout) clearTimeout(visibilityTimeout);
		if (browser) {
			document.removeEventListener('visibilitychange', onVisibilityChange);
		}
	});

	async function checkForUpdate() {
		if (!navigator.onLine) return;
		try {
			const registration = await navigator.serviceWorker.getRegistration(swScope);
			swRegistration = registration ?? null;
			if (!canUpdateServiceWorker(registration)) return;
			await registration.update();
		} catch (error) {
			if (isServiceWorkerUpdateNetworkError(error, navigator.onLine)) {
				console.warn('Service worker update check skipped (network unavailable):', error);
			} else {
				console.error('Failed to update service worker:', error);
			}
		}
	}

	function requestUpdate() {
		if (!alert) {
			alert = true;

			if (!isWidget) {
				showToast({
					text: $t('upgrade.title'),
					fixed: true,
					goto: () => update()
				});
			} else {
				// update();
			}
		}
	}

	function update() {
		const waiting = swRegistration?.waiting;
		if (waiting) {
			waiting.postMessage({ type: 'SKIP_WAITING' });
		} else if (isWidget) {
			resetWidget();
		} else {
			window.location.reload();
		}
	}

	function bindRegistrationListeners(registration: ServiceWorkerRegistration) {
		// 1. Update is already waiting when the page loads: activate immediately without prompting
		if (registration.waiting) {
			registration.waiting.postMessage({ type: 'SKIP_WAITING' });
		}

		// 2. Update is detected while the user is browsing
		registration.addEventListener('updatefound', () => {
			const newWorker = registration.installing;
			newWorker?.addEventListener('statechange', () => {
				if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
					requestUpdate();
				}
			});
		});
	}

	async function registerWorker(): Promise<ServiceWorkerRegistration> {
		const registration = await navigator.serviceWorker.register('/service-worker.js', {
			type: dev ? 'module' : 'classic',
			scope: swScope
		});
		bindRegistrationListeners(registration);
		return registration;
	}

	onMount(async () => {
		if ('serviceWorker' in navigator) {
			let hadController = Boolean(navigator.serviceWorker.controller);

			// Update listener registered before awaiting registration to avoid missing immediate claim()
			navigator.serviceWorker.addEventListener('controllerchange', () => {
				// Triggered because message SKIP_WAITING is received by the SW, and it updates the SW
				if (refreshing) return;
				// Avoid reloading on initial activation on a browser without a prior SW controller
				if (!hadController) {
					hadController = true;
					return;
				}
				refreshing = true;
				if (isWidget) {
					resetWidget();
				} else {
					window.location.reload();
				}
			});

			// Install
			try {
				swRegistration = await registerWorker();
			} catch (error) {
				if (isServiceWorkerUpdateNetworkError(error, navigator.onLine)) {
					console.warn(
						'Service worker registration skipped (network unavailable):',
						error
					);
				} else if (navigator.onLine) {
					console.error('🛑 Registration failed:', error);
				}
				// In local development, switching between dev ('module') and build ('classic')
				// requires unregistering previous workers before retrying.
				if (dev) {
					const registrations = await navigator.serviceWorker.getRegistrations();
					for (const registration of registrations) {
						await registration.unregister();
					}
					hadController = false;

					try {
						swRegistration = await registerWorker();
					} catch (retryError) {
						console.error('🛑 Re-registration failed:', retryError);
					}
				}
			}

			// Check for updates periodically (every 5 min) and when returning to the tab
			if (swRegistration) {
				versionCheckInterval = setInterval(checkForUpdate, 1000 * 60 * 5);
			}

			document.addEventListener('visibilitychange', onVisibilityChange);
		}
	});
</script>
